import { JobDetailsFields } from './JobDetailsFields';
import { createJobDetailsSchema } from '../applicant/schema';
import { ApplicationData, ApplicationDataStore } from '../application/ApplicationDataStore';
import { SaveFormDataToStore } from '../application/SaveFormDataToStore';
import { Form, FormProps } from '../common/Form';
import { z } from 'zod';
import { useMemo } from 'react';
import {
    useProviderConfig,
    useSecondLookIntegration,
} from '@servicetitan/financing-workflow-shared';

export const Container = (
    props: Omit<
        FormProps<ApplicationData['applicant']>,
        'schema' | 'defaultValues' | 'formData' | 'children'
    >,
) => {
    const { applicationData } = ApplicationDataStore.useStore();
    const providerConfig = useProviderConfig();
    const {
        jobDetailsFieldOverrides = {},
        statesWithSpouseInformation = [],
        hasDetailedMarriageFields = false,
        addressValidation,
    } = providerConfig;
    const secondLookIntegration = useSecondLookIntegration();

    // Check if mortgage payment/balance fields are in the field overrides
    const requireMortgageDetails =
        jobDetailsFieldOverrides.additions?.some(
            f =>
                f === 'mortgageMonthlyPayment' ||
                f === 'mortgageEstimatedBalance' ||
                f === 'isManufacturedHome',
        ) ?? false;

    // Check if improvement type is in the field overrides (for Service Finance flow)
    const useImprovementTypeInJobDetails =
        jobDetailsFieldOverrides.additions?.some(f => f === 'improvementType') ?? false;

    // Check if industry type is in the field overrides (for Turns flow)
    const useIndustryTypeInJobDetails =
        jobDetailsFieldOverrides.additions?.some(f => f === 'industryType') ?? false;

    // Require industry type if:
    // 1. Provider config explicitly adds it (e.g., Turns as primary provider), OR
    // 2. Turns integration is enabled for waterfall/second-look flows
    // BUT NOT if Turns service is degraded (field would be hidden)
    const requireIndustryType =
        (useIndustryTypeInJobDetails || secondLookIntegration.isEnabled) &&
        !secondLookIntegration.isServiceDegraded;

    const { secondLookProviderConfig } = secondLookIntegration;

    const schema = useMemo(() => {
        const baseSchema = z.object({
            primaryApplicant: z.object({
                jobDetails: createJobDetailsSchema(
                    statesWithSpouseInformation,
                    requireMortgageDetails,
                    requireIndustryType,
                    useImprovementTypeInJobDetails,
                    hasDetailedMarriageFields,
                    addressValidation,
                    // When a waterfall second-look config is present, use it to widen the residence
                    // value set. When the current provider is itself a second-look provider (e.g.
                    // Finturf via the prequals banner), fall back to its own fieldValueSets so its
                    // extended residence options are accepted by validation.
                    secondLookProviderConfig ?? providerConfig,
                ),
            }),
        });

        // Add improvement field when using improvement type instead of industry type (for first-look submission)
        if (useImprovementTypeInJobDetails) {
            return baseSchema.extend({
                // eslint-disable-next-line @typescript-eslint/naming-convention
                improvement: z.string({ required_error: 'Select an improvement type' }),
            });
        }

        return baseSchema;
    }, [
        statesWithSpouseInformation,
        requireMortgageDetails,
        requireIndustryType,
        useImprovementTypeInJobDetails,
        hasDetailedMarriageFields,
        addressValidation,
        secondLookProviderConfig,
        providerConfig,
    ]);

    return (
        <Form {...props} formData={applicationData.applicant!} schema={schema}>
            <SaveFormDataToStore fieldPrefix="applicant" />
            <JobDetailsFields fieldPrefix="primaryApplicant" />
        </Form>
    );
};
